import { Controller } from "@hotwired/stimulus";

// Mobile drawer for the dashboard sidebar plus the active-item
// indicator's slide animation. Catalyst's MobileSidebar uses
// Headless.Dialog (focus trap, ESC, click-outside, portal) and
// motion/react's <LayoutGroup> for the indicator FLIP. We don't ship
// either; this controller wires equivalents against a single <nav>
// driven by the `data-sidebar-open` attribute, and positions a single
// indicator per section based on whichever item carries
// `data-current="true"`.
//
// Targets:
//   - panel     — the <nav> element. Open state is `data-sidebar-open`.
//   - backdrop  — overlay behind the drawer on mobile.
//   - link      — sidebar links that get `data-current` when active.
//   - trigger   — hamburger button. `aria-expanded` and `aria-label`
//                 mirror drawer state for assistive tech.
//   - indicator — left-rail span per section, positioned to mirror the
//                 current item's offset within that section.
//
// On `>= lg`, CSS pins the panel via `lg:translate-x-0` regardless of
// the attribute, so toggle calls have no visible effect at that width.
export default class extends Controller {
  static targets = ["panel", "link", "backdrop", "trigger", "indicator"];

  #onNavigate;
  #onKeydown;

  connect() {
    this.#highlightActiveLink();
    // Initial position lands without animation — items already have
    // their final layout positions when the controller mounts.
    this.#updateIndicators({ animate: false });

    this.#onNavigate = this.#handleNavigate.bind(this);
    this.#onKeydown = this.#handleKeydown.bind(this);
    document.addEventListener("turbo:load", this.#onNavigate);
    document.addEventListener("turbo:frame-load", this.#onNavigate);
    document.addEventListener("keydown", this.#onKeydown);
  }

  disconnect() {
    document.removeEventListener("turbo:load", this.#onNavigate);
    document.removeEventListener("turbo:frame-load", this.#onNavigate);
    document.removeEventListener("keydown", this.#onKeydown);
  }

  toggle() {
    this.#setOpen(!this.#isOpen());
  }

  #isOpen() {
    return this.panelTarget.hasAttribute("data-sidebar-open");
  }

  #setOpen(open) {
    if (open) {
      this.panelTarget.setAttribute("data-sidebar-open", "");
    } else {
      this.panelTarget.removeAttribute("data-sidebar-open");
    }
    if (this.hasTriggerTarget) {
      this.triggerTarget.setAttribute("aria-expanded", String(open));
      this.triggerTarget.setAttribute(
        "aria-label",
        open ? "Close sidebar" : "Open sidebar",
      );
    }
    this.#showBackdrop(open);
  }

  #handleNavigate() {
    this.#setOpen(false);
    this.#highlightActiveLink();
    this.#updateIndicators();
  }

  #handleKeydown(event) {
    if (event.key === "Escape" && this.#isOpen()) {
      this.#setOpen(false);
    }
  }

  #showBackdrop(show) {
    if (!this.hasBackdropTarget) return;
    if (show) {
      this.backdropTarget.setAttribute("data-sidebar-open", "");
    } else {
      this.backdropTarget.removeAttribute("data-sidebar-open");
    }
  }

  #highlightActiveLink() {
    const path = window.location.pathname;
    this.linkTargets.forEach((link) => {
      const active =
        link.pathname === path || path.startsWith(link.pathname + "/");
      // Drives Catalyst's `data-current` attribute so the
      // `data-current:*:data-[slot=icon]:fill-*` rules engage.
      if (active) {
        link.setAttribute("data-current", "true");
      } else {
        link.removeAttribute("data-current");
      }
    });
  }

  // Position each section's indicator over its current item. The
  // indicator's `top` and `height` are inline-styled; the CSS
  // `transition-[top,height,opacity]` on the span animates the move.
  // `inset-y-2` (8px top + 8px bottom) is folded into the math so the
  // indicator sits visually inside the item's vertical padding.
  #updateIndicators({ animate = true } = {}) {
    if (!this.hasIndicatorTarget) return;

    this.indicatorTargets.forEach((indicator) => {
      const section = indicator.parentElement;
      const currentItem = section?.querySelector('[data-current="true"]');

      if (!currentItem) {
        indicator.style.opacity = "0";
        return;
      }

      if (!animate) {
        indicator.style.transition = "none";
      }

      indicator.style.top = `${currentItem.offsetTop + 8}px`;
      indicator.style.height = `${currentItem.offsetHeight - 16}px`;
      indicator.style.opacity = "1";

      if (!animate) {
        // Force layout, then re-enable the CSS transition next frame
        // so future changes animate.
        // eslint-disable-next-line no-unused-expressions
        indicator.offsetHeight;
        requestAnimationFrame(() => {
          indicator.style.transition = "";
        });
      }
    });
  }
}
